import defaultBusinessHours from "@/data/business-hours.json"

export type HolidayObservance = "full-closure" | "half-day"

export interface Holiday {
  /** Calendar date in the settings timezone, `YYYY-MM-DD`. */
  date: string
  /** Editor-facing label, e.g. "Christmas Day", "Sechseläuten". */
  label: string
  observance: HolidayObservance
}

export interface BusinessHoursSettings {
  /** IANA timezone, e.g. `Europe/Zurich`. */
  timezone: string
  /** Open weekdays as `Date.getDay()` values (0 = Sun … 6 = Sat). */
  weekdays: number[]
  /** Open hour, 24h (e.g. 9). */
  startHour: number
  /** Close hour, 24h (e.g. 17). */
  endHour: number
  /** Early-close hour applied on `half-day` holidays (e.g. 13). */
  halfDayEndHour: number
  /** Support phone (E.164-ish). Not CMS-managed; always from the default. */
  phone: string
  holidays: Holiday[]
}

/** The Swiss baseline, used as the fallback when a country has no CMS record. */
export const DEFAULT_BUSINESS_HOURS: BusinessHoursSettings =
  defaultBusinessHours as BusinessHoursSettings

/**
 * Whether `now` falls inside support business hours for the given settings.
 *
 * Pure + timezone-correct: callers pass the resolved settings and (in tests)
 * a fixed `now`. The holiday date is computed from the tz-localized date — the
 * old implementation compared against `toISOString()` (UTC), which mis-dated
 * holidays for a few hours around midnight Zürich time. Half-day holidays are
 * open from `startHour` until `halfDayEndHour`; full-closure days are closed.
 */
export function isBusinessHours(
  settings: BusinessHoursSettings,
  now: Date = new Date()
): boolean {
  return businessHoursVerdict(settings, now) ?? false
}

/**
 * The same schedule check, but honest about not knowing: `null` when this
 * engine cannot place `now` in the settings timezone.
 *
 * The old implementation round-tripped through
 * `new Date(now.toLocaleString("en-US", { timeZone }))` and returned `false`
 * on any failure. That parse is fragile — ICU 72 put a narrow no-break space
 * before "AM"/"PM" and the string stopped parsing, giving an Invalid Date
 * whose `getDay()` is NaN, so `weekdays.includes(NaN)` read as "closed" on a
 * weekday morning. The chat panel forwards that verdict to glue as
 * authoritative (`support_open`), so the bot told visitors inside office
 * hours that nobody was reachable (three handoffs 2026-09-09/10). Weekday,
 * hour and date are now read from `Intl.DateTimeFormat#formatToParts`, which
 * never goes through a string parse; `null` remains only for an engine that
 * throws on the zone itself, and then the panel sends no `support_open` and
 * glue applies the same CMS schedule server-side.
 */
export function businessHoursVerdict(
  settings: BusinessHoursSettings,
  now: Date = new Date()
): boolean | null {
  if (Number.isNaN(now.getTime())) return null
  let parts: Intl.DateTimeFormatPart[]
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone: settings.timezone,
      hourCycle: "h23",
      weekday: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
    }).formatToParts(now)
  } catch {
    return null
  }
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value
  const day = WEEKDAY_INDEX[get("weekday") ?? ""]
  const hour = Number(get("hour"))
  const dateKey = `${get("year")}-${get("month")}-${get("day")}`
  if (day === undefined || Number.isNaN(hour)) return null

  if (!settings.weekdays.includes(day)) return false

  const holiday = settings.holidays.find((h) => h.date === dateKey)
  if (holiday) {
    if (holiday.observance === "full-closure") return false
    return hour >= settings.startHour && hour < settings.halfDayEndHour
  }

  return hour >= settings.startHour && hour < settings.endHour
}

/** `Date.getDay()` values for the en-US short weekday names `formatToParts` yields. */
const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

/**
 * Whether the `ahead-chat-force-hours` sessionStorage QA override may act on
 * this host. Nothing in the repo writes the key any more (the `/dev/chat` page
 * and the chat e2e that did were removed 2026-09-08), but the hook still reads
 * it, and a value set from devtools outlives the tab's navigation. Production
 * hosts ignore it; localhost, preview deploys and `*.aheadhealth.dev` keep it
 * for QA.
 */
export function qaOverrideAllowed(hostname: string): boolean {
  const h = hostname.toLowerCase()
  return !(h === "aheadhealth.com" || h.endsWith(".aheadhealth.com"))
}
